import { Link } from 'react-router-dom';
import type { EvidenceLink, EvidenceRelation, FindingSource } from '../api/types';
import {
  availabilityMeta,
  evidenceClassLabel,
  evidenceClassMeta,
  formatDate,
  isSafeExternalUrl,
  relationMeta,
  sourceKindLabel,
} from '../lib/format';

export interface EvidenceTally {
  supports: number;
  contradicts: number;
  context: number;
  limiting: number;
}

export function tallyEvidence(evidence: EvidenceLink[]): EvidenceTally {
  const tally: EvidenceTally = { supports: 0, contradicts: 0, context: 0, limiting: 0 };
  for (const link of evidence) {
    tally[link.relation] += 1;
    if (link.metadata?.limiting === true) tally.limiting += 1;
  }
  return tally;
}

/** A compact, text-first count of evidence by relation. Counts come straight from the finding's links. */
export function EvidenceTallyLine({ evidence }: { evidence: EvidenceLink[] }) {
  const tally = tallyEvidence(evidence);
  return (
    <p className="tally">
      <span className="tally__item tally__item--supports">{tally.supports} supporting</span>
      <span className="tally__item tally__item--contradicts">{tally.contradicts} conflicting</span>
      <span className="tally__item tally__item--context">{tally.context} context</span>
      {tally.limiting > 0 && <span className="tally__item tally__item--limiting">{tally.limiting} limiting</span>}
    </p>
  );
}

function Locator({ link }: { link: EvidenceLink }) {
  const locator = link.metadata?.locator;
  if (locator?.path === undefined) return null;
  const lines =
    locator.lineStart === undefined
      ? ''
      : locator.lineEnd !== undefined && locator.lineEnd !== locator.lineStart
        ? ` · lines ${locator.lineStart}–${locator.lineEnd}`
        : ` · line ${locator.lineStart}`;
  return (
    <span className="evidence__locator">
      {locator.path}
      {lines}
    </span>
  );
}

function EvidenceItem({ link, source }: { link: EvidenceLink; source: FindingSource | undefined }) {
  const evidenceClass = link.metadata?.evidenceClass;
  const classLabel = evidenceClassLabel(evidenceClass);
  const caution = evidenceClass === undefined ? undefined : evidenceClassMeta[evidenceClass]?.caution;
  const version = source?.versions?.find((candidate) => candidate.id === link.versionId);
  const availability = source === undefined ? null : availabilityMeta[source.availability];
  const pinned = link.versionId !== undefined;

  return (
    <li className={`evidence evidence--${link.relation}`}>
      <div className="evidence__head">
        {source === undefined ? (
          <span className="evidence__title evidence__title--restricted">
            Source not visible to your account
            <span className="visually-hidden"> ({link.sourceId})</span>
          </span>
        ) : (
          <Link className="evidence__title" to={`/sources/${encodeURIComponent(source.id)}`}>
            {source.title}
          </Link>
        )}
        <span className="evidence__tags">
          {source !== undefined && <span className="tag">{sourceKindLabel[source.kind]}</span>}
          {classLabel !== null && <span className="tag tag--class">{classLabel}</span>}
          {link.metadata?.limiting === true && <span className="tag tag--limiting">Limiting</span>}
          {availability !== null && availability.tone !== 'ok' && (
            <span className={`tag tag--${availability.tone}`}>{availability.label}</span>
          )}
        </span>
      </div>
      {link.note !== undefined && link.note !== '' && <p className="evidence__note">{link.note}</p>}
      {caution !== undefined && <p className="evidence__caution">{caution}</p>}
      <p className="evidence__meta">
        {pinned ? (
          <span>
            Pinned version{version?.observedAt !== undefined ? ` observed ${formatDate(version.observedAt)}` : ''}
          </span>
        ) : (
          <span>No original version imported</span>
        )}
        {version?.sourceEditedAt !== undefined && <span>Edited at source {formatDate(version.sourceEditedAt)}</span>}
        <Locator link={link} />
        {source !== undefined && isSafeExternalUrl(source.url) && (
          <a href={source.url} rel="noreferrer noopener" target="_blank" className="evidence__external">
            Open at source<span className="visually-hidden"> (opens in a new tab; requires your own access)</span>
          </a>
        )}
      </p>
    </li>
  );
}

const ORDER: EvidenceRelation[] = ['supports', 'contradicts', 'context'];

const EMPTY: Record<EvidenceRelation, string> = {
  supports: 'No supporting evidence is linked to this version.',
  contradicts:
    'No directly conflicting evidence is linked. That is not the same as the claim being settled: read the counterexamples and uncertainty.',
  context: 'No context sources are linked.',
};

export function EvidenceSections({
  evidence,
  sources,
  headingLevel = 3,
}: {
  evidence: EvidenceLink[];
  sources: FindingSource[];
  headingLevel?: 2 | 3;
}) {
  const byId = new Map(sources.map((source) => [source.id, source]));
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div className="evidence-sections">
      {ORDER.map((relation) => {
        const links = evidence.filter((link) => link.relation === relation);
        return (
          <section key={relation} className={`evidence-group evidence-group--${relation}`} aria-labelledby={`ev-${relation}`}>
            <Heading id={`ev-${relation}`} className="evidence-group__title">
              {relationMeta[relation].plural}
              <span className="evidence-group__count">{links.length}</span>
            </Heading>
            <p className="evidence-group__description">{relationMeta[relation].description}</p>
            {links.length === 0 ? (
              <p className="muted">{EMPTY[relation]}</p>
            ) : (
              <ol className="evidence-list">
                {links.map((link, index) => (
                  <EvidenceItem
                    // Evidence links have no ID of their own; the same source can be cited twice with different notes.
                    key={`${link.sourceId}|${link.versionId ?? ''}|${link.note ?? ''}|${String(index)}`}
                    link={link}
                    source={byId.get(link.sourceId)}
                  />
                ))}
              </ol>
            )}
          </section>
        );
      })}
    </div>
  );
}
